// src/pages/Media/Media.jsx
import React, { useEffect, useRef, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import CustomAudioPlayer from "../../components/CustomAudioPlayer/CustomAudioPlayer";

// MP3
import trackLuvulla from "../../assets/audio/Bilebandi_Joyride_-_2080-luvulla.mp3";

// Nopeat biisit näytetään ensin.
const videos = [
  {
    title: "Nopeat biisit",
    description: "Tanssilattiaa nostavaa bilemeininkiä aidosta keikkatilanteesta.",
    src: "https://www.youtube.com/embed/97PB7ksSqHY?si=c_94z8_4K-Icitcm",
    eventKey: "nopeat_biisit"
  },
  {
    title: "Hitaat biisit",
    description: "Tunnelmallisempaa livefiilistä häihin ja juhliin.",
    src: "https://www.youtube.com/embed/m7fNg_ViaFQ?si=nc37IFP04lPBqvlZ",
    eventKey: "hitaat_biisit"
  }
];

const soundCloudTracks = [
  {
    title: "Rebel Yell",
    embedSrc:
      "https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/1418246374&color=%23ff5500",
    link: "https://soundcloud.com/spiderlegs-productions/bilebandi-joyride-rebel-yell"
  },
  {
    title: "Teuvo, maanteiden kuningas",
    embedSrc:
      "https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/1397193199&color=%23ff5500",
    link: "https://soundcloud.com/spiderlegs-productions/teuvo-maanteiden-kuningas"
  },
  {
    title: "Paratiisi",
    embedSrc:
      "https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/1105936846&color=%23ff5500",
    link: "https://soundcloud.com/spiderlegs-productions/paratiisi"
  }
];

export default function Media() {
  const mediaHeadingRef = useRef(null);
  const [showMoreAudio, setShowMoreAudio] = useState(false);

  useEffect(() => {
    AOS.init({ once: true, duration: 650 });

    const heading = mediaHeadingRef.current;

    // Mittausta ei tehdä pelkän komponentin latautumisen perusteella.
    if (!heading || typeof window.IntersectionObserver !== "function") {
      return;
    }

    // Otsikon seuraaminen toimii riippumatta lisänäytteiden avaamisesta.
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (
          !entry ||
          !entry.isIntersecting ||
          entry.intersectionRatio < 0.5
        ) {
          return;
        }

        // Tapahtuma lähetetään, kun vähintään puolet otsikosta näkyy.
        if (typeof window.gaEvent === "function") {
          window.gaEvent("media_katsottu");
        }

        // Yksi näkyvyystapahtuma tämän komponentin näyttökertaa kohden.
        observer.disconnect();
      },
      { threshold: 0.5 }
    );

    observer.observe(heading);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Lisänäytteiden avaaminen muuttaa alempien osioiden sijaintia.
    AOS.refresh();
  }, [showMoreAudio]);

  const ga = (name, data = {}) => {
    if (typeof window.gaEvent === "function") {
      window.gaEvent(name, data);
    }
  };

  return (
    <section
      id="media"
      aria-labelledby="media-heading"
      className="relative w-full overflow-hidden bg-joyrideLight py-12 transition-colors md:py-28 dark:bg-joyrideNavy"
    >
      {/* Taustan värivalot */}
      <div className="pointer-events-none absolute -left-28 top-16 h-72 w-72 rounded-full bg-joyrideCyan/15 blur-3xl" />
      <div className="pointer-events-none absolute -right-28 bottom-20 h-80 w-80 rounded-full bg-joyridePurple/20 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 md:px-8">
        {/* Osion esittely */}
        <div
          data-aos="fade-down"
          className="mx-auto mb-8 max-w-3xl text-center md:mb-12"
        >
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-joyridePurple dark:text-joyrideCyan">
            Katso ja kuuntele
          </p>

          <h2
            ref={mediaHeadingRef}
            id="media-heading"
            className="font-lobster text-4xl font-bold text-joyridePurple md:text-5xl dark:text-joyrideCyan dark:text-shadow-lg"
          >
            Bilebändi Joyride livenä
          </h2>

          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-joyrideNavy/80 md:text-lg dark:text-joyrideLight/80">
            Videoita, livemusiikkitallenteita ja ääninäytteitä aidoista
            keikkatilanteista. Näistä saat kuvan Joyriden soundista,
            tunnelmasta ja esiintymistyylistä.
          </p>
        </div>

        {/* Videot: mobiilissa allekkain, leveällä näytöllä rinnakkain */}
        <div className="grid gap-6 md:gap-8 lg:grid-cols-2">
          {videos.map((video) => (
            <article
              key={video.eventKey}
              data-aos="fade-up"
              className="group relative overflow-hidden rounded-[2rem] border border-black/5 bg-white/75 p-2 shadow-xl backdrop-blur-sm transition hover:-translate-y-1 hover:shadow-2xl sm:p-3 dark:border-white/10 dark:bg-white/5"
            >
              <div className="pointer-events-none absolute -inset-1 rounded-[2rem] bg-gradient-to-br from-joyrideCyan/15 via-transparent to-joyridePurple/20 opacity-0 blur-2xl transition group-hover:opacity-100" />

              <div className="relative aspect-video overflow-hidden rounded-[1.5rem]">
                <iframe
                  className="h-full w-full"
                  src={video.src}
                  title={`Bilebändi Joyride – ${video.title}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                  onLoad={() => ga("video_ladattu", { video: video.eventKey })}
                />
              </div>

              <div className="relative px-2 pb-2 pt-4">
                <h3 className="font-lobster text-2xl text-joyridePurple dark:text-joyrideCyan">
                  {video.title}
                </h3>

                <p className="mt-1 text-sm leading-relaxed text-joyrideNavy/75 dark:text-joyrideLight/75 text-center">
                  {video.description}
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* MP3-soitin säilyy heti näkyvissä */}
        <div
          data-aos="fade-up"
          className="mt-6 overflow-hidden rounded-[2rem] border border-black/5 bg-white/75 p-4 shadow-xl backdrop-blur-sm md:mt-10 md:p-6 dark:border-white/10 dark:bg-white/5"
        >
          <h3 className="mb-5 font-lobster text-3xl text-joyridePurple dark:text-joyrideCyan">
            Ääninäyte
          </h3>

          <CustomAudioPlayer
            src={trackLuvulla}
            title="Bilebändi Joyride – 2080-luvulla"
            onPlay={() => ga("mp3_toisto_aloitettu", { kappale: "2080-luvulla" })}
          />
        </div>

        {/* Lisänäytteiden avaus toimii myös näppäimistöllä */}
        <button
          type="button"
          aria-expanded={showMoreAudio}
          aria-controls="media-extra-audio"
          onClick={() => setShowMoreAudio((prev) => !prev)}
          className="mt-6 flex w-full items-center justify-between gap-4 rounded-2xl border border-black/10 bg-white/75 px-5 py-4 text-left font-semibold text-joyrideNavy shadow-lg transition hover:border-joyridePurple/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-joyridePurple md:mt-10 dark:border-white/10 dark:bg-white/5 dark:text-joyrideLight dark:focus-visible:ring-joyrideCyan"
        >
          <span>Lisää ääninäytteitä ({soundCloudTracks.length})</span>

          <span aria-hidden="true" className="text-2xl leading-none">
            {showMoreAudio ? "−" : "+"}
          </span>
        </button>

        {/* SoundCloud-soittimet ladataan vasta, kun lisänäytteet avataan. */}
        <div
          id="media-extra-audio"
          className={
            showMoreAudio
              ? "mt-5 grid gap-4 md:gap-6 lg:grid-cols-3"
              : "hidden"
          }
        >
          {showMoreAudio &&
            soundCloudTracks.map((item) => (
              <article
                key={item.title}
                className="overflow-hidden rounded-[2rem] border border-black/5 bg-white/75 p-4 shadow-lg backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-white/5"
              >
                <h3 className="mb-3 font-lobster text-2xl text-joyridePurple dark:text-joyrideCyan">
                  {item.title}
                </h3>

                <iframe
                  title={`Kuuntele Joyride – ${item.title}`}
                  aria-label={`Kuuntele Joyride – ${item.title}`}
                  width="100%"
                  height="166"
                  scrolling="no"
                  frameBorder="no"
                  src={item.embedSrc}
                  loading="lazy"
                  onLoad={() =>
                    ga("soundcloud_ladattu", { kappale: item.title })
                  }
                />

                <p className="mt-3 text-sm font-light leading-relaxed text-joyrideNavy dark:text-joyrideLight">
                  <a
                    href="https://soundcloud.com/spiderlegs-productions"
                    className="underline underline-offset-4 transition hover:text-joyrideCyan"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => ga("soundcloud_profiili_klikattu")}
                  >
                    Spiderlegs Productions
                  </a>{" "}
                  –{" "}
                  <a
                    href={item.link}
                    className="underline underline-offset-4 transition hover:text-joyrideCyan"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      ga("soundcloud_kappale_klikattu", {
                        kappale: item.title
                      })
                    }
                  >
                    Kuuntele Joyride – {item.title}
                  </a>
                </p>
              </article>
            ))}
        </div>
      </div>
    </section>
  );
}